import type { BookingStage, GarmentState } from '../api/bookings';
import type { StatusTone } from '../vocabulary';

/**
 * What colour each booking and garment state reads as, decided once.
 *
 * <p>
 * Shared rather than mapped per screen for the reason {@link StatusBadge} itself is shared: the
 * register, the detail page, the issue counter and the return counter all show the same states, and
 * one that is amber on three of them and grey on the fourth teaches an operator to stop reading the
 * colour at all. There are five screens now and Section 9 brings more.
 * </p>
 *
 * <p>
 * <strong>The tones say what the row means to the shop, not what is technically happening.</strong>
 * `Issued` is `brand` rather than `positive` because a garment out at a wedding is money at risk,
 * not a success; `Returned` is `neutral` because coming back is not an outcome until somebody has
 * looked at it. The three exits are all `negative` even though `Completed` and `Cancelled` are both
 * "finished" — one of them lost the shop a booking.
 * </p>
 */

const stageTones: Record<BookingStage, StatusTone> = {
  Enquiry: 'neutral',
  Quotation: 'neutral',
  // A hold is the one stage with a clock on it, and a lapsed one silently returns stock to the rail.
  Hold: 'warning',
  Confirmed: 'brand',
  InProgress: 'brand',
  Completed: 'positive',
  Cancelled: 'negative',
  Expired: 'negative',
  Recovery: 'negative',
};

export function stageTone(stage: BookingStage): StatusTone {
  return stageTones[stage];
}

const garmentTones: Record<GarmentState, StatusTone> = {
  Pending: 'neutral',
  Allocated: 'neutral',
  Fitting: 'neutral',
  // The only state that needs somebody to do something before a pickup date arrives.
  Alteration: 'warning',
  Ready: 'positive',
  Issued: 'brand',
  Returned: 'neutral',
  Inspected: 'neutral',
  NotReturned: 'negative',
  // The two amendment tombstones. Neutral, not negative: a garment the customer swapped or dropped
  // is not a failure, it is a line that stopped applying — and these rows are rendered greyed
  // *beside* the live ones rather than hidden, so a loud colour would draw the eye to exactly the
  // rows nobody needs to act on.
  Replaced: 'neutral',
  Removed: 'neutral',
};

export function garmentTone(state: GarmentState): StatusTone {
  return garmentTones[state];
}

/**
 * How a wearer's readiness reads — "2 of 3 ready".
 *
 * <p>
 * <strong>Partly ready is `warning`, not `positive`.</strong> A person collects a whole outfit, so
 * two garments of three is not two-thirds done — it is nobody able to leave the shop dressed. That
 * is the same argument the register's readiness column makes by counting wearers rather than
 * garments, and it has to agree here or the two screens contradict each other.
 * </p>
 */
export function readinessTone(readyCount: number, garmentCount: number): StatusTone {
  if (garmentCount === 0) {
    return 'neutral';
  }

  if (readyCount >= garmentCount) {
    return 'positive';
  }

  return readyCount === 0 ? 'neutral' : 'warning';
}

/**
 * Whether a garment can be handed over — the server's `GoingOutAsync`, mirrored exactly.
 *
 * <p>
 * <strong>Set aside is enough; marked Ready is not required.</strong> A garment reserved for the
 * booking (`Allocated`) or back from a fitting can go out of the door. Until 2026-10-17 the clients
 * offered `Ready` alone while the server took all three, so a booking nobody had walked through the
 * Tomorrow list read "Nothing ready yet" at the counter with its suit on the rail and the customer
 * standing there. Ready is still the wardrobe's tick the day before; it is no longer a gate.
 * </p>
 *
 * <p>
 * Only the tailor (`Alteration`) and an enquiry's unreserved line (`Pending`) hold a garment back.
 * </p>
 */
export function isReadyToIssue(state: GarmentState): boolean {
  return state === 'Ready' || state === 'Allocated' || state === 'Fitting';
}

/**
 * Whether this garment has left the shop and not come back.
 *
 * Used for the deposit's exposure and for the overdue read; `NotReturned` is deliberately excluded
 * because a written-off garment is no longer expected and counting it as outstanding would keep an
 * order looking live forever.
 */
export function isOutOnRent(state: GarmentState): boolean {
  return state === 'Issued';
}

/**
 * The two amendment tombstones: a garment that is on the row but not on the booking.
 *
 * <p>
 * The server's own `BookingService.IsOffTheBooking`, restated here so screens stop inlining the
 * two state names. They are deliberately <em>not</em> a soft delete — a dropped garment was held,
 * refused to other customers and possibly altered — so the rows are still returned and rendered
 * greyed, which is exactly why every reader that means "the garments this booking is for" has to
 * ask rather than assume.
 * </p>
 *
 * <p>
 * <strong>The single copy, and it lives here because this module is a leaf</strong> — it imports
 * nothing but types. There was a second, identical one in `amendment.ts`, which the print module
 * and the morning screen would both have had to reach past `discount`, `pricing` and `rail` to
 * borrow. Two definitions of the server's one predicate is how the two halves eventually disagree
 * about which garments a booking is for.
 * </p>
 */
export function isOffTheBooking(state: GarmentState): boolean {
  return state === 'Removed' || state === 'Replaced';
}
